<template>
  <v-container fluid>
    <v-row>
      <h1 class="primary--text">
        {{ $t("admin.productsDatabase.title") }}
      </h1>
    </v-row>
    <v-row>
      <v-col>
        <v-btn
          color="primary"
          class="mr-2 white--text"
          @click="createNewBook"
        >
          {{ $t("admin.productsDatabase.createPublication") }}
        </v-btn>
        <v-btn
          color="secondary"
          class="mr-2 white--text"
          @click="showAuthorsDialog = true"
        >
          {{ $t("admin.productsDatabase.viewAuthorDatabase") }}
        </v-btn>
        <v-btn
          color="secondary"
          class="white--text"
          @click="showPublishersDialog = true"
        >
          {{ $t("admin.productsDatabase.viewPublisherDatabase") }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="8"
      >
        <v-text-field
          v-model="searchQuery"
          :label="$t('admin.productsDatabase.searchPlaceholder')"
          prepend-icon="mdi-magnify"
          outlined
          dense
          @input="debounceSearch"
        />
      </v-col>
    </v-row>

    <ekitabu-data-table
      :headers="headers"
      :items="localBooks"
      :search="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :loading="loading"
      :server-items-length="totalBooks"
      :page.sync="page"
      :items-per-page="itemsPerPage"
      :footer-props="{
        'items-per-page-text': $t('common.itemsPerPage'),
        'items-per-page-options': [5, 10, 25, 50]
      }"
      class="elevation-1"
      @update:items-per-page="handleItemsPerPageChange"
      @pagination="handlePageChange"
      @update:sort-by="handleSortChange"
      @update:sort-desc="handleSortChange"
    >
      <template #item.format="{ item }">
        <v-chip
          outlined
          small
        >
          {{ item?.format?._id.toUpperCase() }}
        </v-chip>
      </template>

      <!-- Authors column -->
      <template #item.authors="{ item }">
        {{ formatAuthors(item.bookAuthors) }}
      </template>

      <!-- ISBN column: muted placeholder makes titles with no ISBN easy to spot -->
      <template #item.isbn="{ item }">
        <span v-if="item.isbn">{{ item.isbn }}</span>
        <span
          v-else
          class="grey--text font-italic"
        >{{ $t("admin.productsDatabase.noIsbn") }}</span>
      </template>

      <!-- Status column -->
      <template #item.status="{ item }">
        <v-chip
          :color="statusColor(getBookStatus(item))"
          dark
          small
        >
          <img
            :src="getStatusIcon(getBookStatus(item))"
            :style="{ width: '16px', height: '16px', marginLeft: '-4px', marginRight: '4px' }"
          >
          {{ $t(`bookInfo.${getBookStatus(item)}`) }}
        </v-chip>
      </template>
      <template #item.price="{ item }">
        <Price
          :book-price="item.price || 0"
          :base-currency="item.currency?._id || 'KES'"
          :show-in-base-currency="true"
          :show-free-text="true"
        />
      </template>

      <!-- Action column: View button (or Edit, etc.) -->
      <template #item.action="{ item }">
        <v-btn
          color="primary"
          small
          @click="viewItem(item)"
        >
          {{ $t("common.view") }}
        </v-btn>
      </template>
    </ekitabu-data-table>

    <!-- Server Pagination -->
    <v-pagination
      v-model="page"
      :length="lastPage"
      :total-visible="7"
      class="mt-4"
      circle
    />
    <v-dialog v-model="showDialog">
      <ProductView
        :book="selectedBook"
        @close="showDialog = false"
        @edit="handleEditProduct"
        @deleted="handleBookDeleted"
      />
    </v-dialog>
    <v-dialog
      v-model="showAuthorsDialog"
      fullscreen
    >
      <AuthorsDatabase @close="showAuthorsDialog = false" />
    </v-dialog>
    <v-dialog
      v-model="showPublishersDialog"
      fullscreen
    >
      <PublishersDatabase @close="showPublishersDialog = false" />
    </v-dialog>

    <v-dialog
      v-if="showImportWizard"
      :value="true"
      fullscreen
      persistent
      @input="val => !val && closeImportWizard()"
    >
      <ProductImportWizard
        :edit-product="selectedBook"
        @close="closeImportWizard"
        @product-created="handleProductCreated"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import LocalBooksQueries from "@/graphql/LocalBookQueries";
import ProductView from './ProductView.vue';
import Price from "@/components/Price.vue";
import AuthorsDatabase from "./AuthorsDatabase.vue";
import PublishersDatabase from "./PublishersDatabase.vue";
import ProductImportWizard from './ProductImportWizard/ProductImportWizard.vue';
import { RELEASE_TYPES } from '@/utils/Const';
import liveIcon from '@/components/icons/publicationStatus/live.png';
import scheduledIcon from '@/components/icons/publicationStatus/scheduled.png';
import unlistedIcon from '@/components/icons/publicationStatus/unlisted.png';

export default {
  components: {
    ProductView,
    Price,
    AuthorsDatabase,
    PublishersDatabase,
    ProductImportWizard,
  },
  data() {
    return {
      search: "",
      sortBy: "title",
      sortDesc: false,
      sortItems: ["Title", "Author", "Format", "Status"],
      page: 1,
      showDialog: false,
      showAuthorsDialog: false,
      showPublishersDialog: false,
      itemsPerPage: 10,
      totalBooks: 0,
      lastPage: 1,
      loading: false,
      localBooks: [],
      headers: [
        { text: this.$t("admin.productsDatabase.titleHeader"), value: "title" },
        {
          text: this.$t("admin.productsDatabase.formatHeader"),
          value: "format",
        },
        {
          text: this.$t("admin.productsDatabase.authorsHeader"),
          value: "authors",
          sortable: false,
        },
        { text: this.$t("admin.productsDatabase.isbnHeader"), value: "isbn" },
        {
          text: this.$t("admin.productsDatabase.publisherHeader"),
          value: "publisher.name",
        },
        { text: this.$t("admin.productsDatabase.priceHeader"), value: "price" },
        {
          text: this.$t("admin.productsDatabase.statusHeader"),
          value: "status",
          sortable: false,
        },
        { text: "", value: "action", sortable: false },
      ],
      searchQuery: "",
      searchTimeout: null,
      fetchSeq: 0,
      selectedBook: null,
      showImportWizard: false,
      preserveSelectedBook: false, // New flag to control selectedBook reset
    };
  },
  created() {
    this.fetchBooks();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
  methods: {
    viewItem(item) {
      this.selectedBook = item;
      this.showDialog = true;
    },
    getBookStatus(book) {
      if (book.availableFrom == null) {
        return RELEASE_TYPES.UNLISTED;
      }

      if (book.availableFrom) {
        const availableFromDate = new Date(book.availableFrom);
        const now = new Date();

        if (availableFromDate > now) {
          return RELEASE_TYPES.SCHEDULED;
        }
      }

      return RELEASE_TYPES.LISTED;
    },
    statusColor(status) {
      switch (status) {
        case RELEASE_TYPES.UNLISTED:
          return "grey";
        case RELEASE_TYPES.SCHEDULED:
          return "blue";
        case RELEASE_TYPES.LISTED:
          return "green";
        default:
          return "primary";
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case RELEASE_TYPES.UNLISTED:
          return unlistedIcon;
        case RELEASE_TYPES.SCHEDULED:
          return scheduledIcon;
        case RELEASE_TYPES.LISTED:
          return liveIcon;
        default:
          return liveIcon;
      }
    },
    formatAuthors(bookAuthors) {
      if (!bookAuthors) return "";
      return bookAuthors
        .map(
          (author) =>
            `${author.author.name} (${author.role.description || "Author"})`
        )
        .join(", ");
    },
    async handleSearch() {
      this.page = 1; // Reset to first page when searching
      await this.fetchBooks();
    },
    async fetchBooks() {
      // Guard against out-of-order responses: only the latest fetch may apply.
      const requestId = ++this.fetchSeq;
      this.loading = true;
      try {
        // Map front-end sortBy values to back-end field names
        const sortByMapping = {
          title: "title",
          format: "format_mimeType",
          "publisher.name": "publisher_name",
          isbn: "isbn",
          price: "price",
        };

        // Get the mapped back-end field name
        const mappedSortBy = this.sortBy ? sortByMapping[this.sortBy] : null;

        // Only construct the order array if mappedSortBy is valid
        const order = mappedSortBy
          ? [{ [mappedSortBy]: this.sortDesc ? "DESC" : "ASC" }]
          : null;

        const result = await this.$apollo.query({
          query: LocalBooksQueries.getLocalBooksForAdminList,
          variables: {
            page: this.page,
            itemsPerPage: this.itemsPerPage,
            simplesearch: this.searchQuery || null,
            order,
            showUnlisted: true,
          },
        });

        if (requestId !== this.fetchSeq) return;

        const { collection, paginationInfo } = result.data.localBooks;
        this.localBooks = collection;
        this.totalBooks = paginationInfo.totalCount;
        this.lastPage = paginationInfo.lastPage;
        this.itemsPerPage = paginationInfo.itemsPerPage;
      } catch (error) {
        if (requestId !== this.fetchSeq) return;
        console.error("Error fetching books:", error);
      } finally {
        if (requestId === this.fetchSeq) {
          this.loading = false;
        }
      }
    },
    handlePageChange() {
      this.fetchBooks();
    },
    handleItemsPerPageChange(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.page = 1; // Reset to first page when changing items per page
      this.fetchBooks();
    },
    debounceSearch(event) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.handleSearch();
      }, 600);
    },
    createNewBook() {
      this.selectedBook = null; // Reset selected book to ensure starting fresh
      this.showImportWizard = true;
    },
    closeImportWizard() {
      this.showImportWizard = false;
      if (!this.preserveSelectedBook) {
        this.selectedBook = null;
      }
      this.preserveSelectedBook = false; // Reset flag
    },
    handleProductCreated(productInfo) {
      // Refresh the product list to include the new product
      this.fetchBooks();

      // We might want to preserve selectedBook after product creation
      this.preserveSelectedBook = true;

      // Show success notification
      this.$store.dispatch('snackbar/showSnackbar', {
        text: this.$t('admin.productsDatabase.productCreatedSuccess'),
        color: 'success'
      });
    },
    handleEditProduct(product) {
      this.selectedBook = product;
      this.showDialog = false;
      this.showImportWizard = true;
    },
    handleBookDeleted() {
      this.showDialog = false;
      this.selectedBook = null;
      this.fetchBooks();
      this.$store.dispatch('snackbar/showSnackbar', {
        text: this.$t('admin.productsDatabase.productView.bookDeleted'),
        color: 'success',
      });
    },
    handleSortChange() {
      this.fetchBooks();
    },
  },
};
</script>

<style scoped>
.v-data-table {
  background-color: var(--v-backgroundSecondary-base);
}

.v-btn {
  margin-bottom: 8px;
}

.v-chip.v-chip--outlined {
  border-color: var(--v-secondary-base);
  color: var (--v-secondary-base);
}

.v-pagination .v-pagination__item--active {
  background-color: var(--v-primary-base) !important;
  color: var(--v-textOnPrimary-base) !important;
}
</style>
